import React from 'react';
import { TrendingUp, Brain, Shield, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import LegalDisclaimer from '../components/LegalDisclaimer';

const About = () => {
  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-purple-600 to-purple-800 rounded-2xl mb-6">
            <span className="text-white font-bold text-4xl">L</span>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">About Neon Alpha</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Your intelligent companion for making informed investment decisions with confidence
          </p>
        </div>

        {/* Mission Statement */}
        <Card className="mb-8">
          <CardContent className="pt-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Mission</h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              Neon Alpha Investment Club democratizes financial education through AI-powered research, premium ebooks, and a private community of self-directed learners. We are an educational platform — not a hedge fund, not an investment advisor, and not a broker.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              Our platform analyzes stocks using 10 proprietary AI signals, processes data from 100+ trading strategies, and delivers actionable trade suggestions tailored to your investment goals and time horizons.
            </p>
          </CardContent>
        </Card>

        {/* Key Features */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Brain className="text-purple-600" size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">AI-Powered Analysis</h3>
                  <p className="text-gray-600">
                    Advanced GPT-4o models analyze market data, sentiment, and technical indicators to generate comprehensive stock insights in real-time.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="text-blue-600" size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Real-Time Market Data</h3>
                  <p className="text-gray-600">
                    Live stock quotes, analyst ratings, and market metrics from trusted sources ensure you're always making decisions with the latest information.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Shield className="text-green-600" size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Secure & Private</h3>
                  <p className="text-gray-600">
                    Your data is encrypted and protected. We never share your trading preferences or watchlist with third parties.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Zap className="text-orange-600" size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Instant Insights</h3>
                  <p className="text-gray-600">
                    Get comprehensive analysis, trade suggestions, and strategy matches in seconds—no manual research required.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* What We Offer */}
        <Card className="mb-8">
          <CardContent className="pt-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">What We Offer</h2>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-white text-sm">✓</span>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">10 Proprietary AI Signals</p>
                  <p className="text-gray-600">Short-term and long-term indicators covering momentum, sentiment, and fundamentals</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-white text-sm">✓</span>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Smart Trade Suggestions</p>
                  <p className="text-gray-600">Options and equity strategies with specific entry points, targets, and time horizons</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-white text-sm">✓</span>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">100+ Trading Strategies</p>
                  <p className="text-gray-600">Curated from professional traders and matched to your selected stocks</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-white text-sm">✓</span>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Daily AI-Curated Picks</p>
                  <p className="text-gray-600">Fresh stock recommendations categorized by timeframe and market direction</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* CTA */}
        <div className="text-center">
          <Button 
            onClick={() => window.location.href = '/dashboard'}
            className="bg-purple-600 hover:bg-purple-700 px-8 py-6 text-lg"
          >
            Start Analyzing Stocks
          </Button>
          <p className="text-sm text-gray-500 mt-4">Free to use • No credit card required</p>
        </div>

        {/* Legal Disclaimer */}
        <LegalDisclaimer variant="full" />
      </div>
    </div>
  );
};

export default About;
